package dev.lingvo.site.components.editor

import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.key
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.site.ds.LVisuallyHiddenStyle
import dev.lingvo.site.i18n.t
import kotlinx.browser.document
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Composable
internal fun EditorComposition(
    workspace: EditorWorkspaceState,
    plan: EditorCompositionPlan,
    announcement: EditorAnnouncement?,
    foundationFailure: (@Composable () -> Unit)?,
    header: @Composable () -> Unit,
    queue: @Composable (measuring: Boolean) -> Unit,
    editor: @Composable (measuring: Boolean) -> Unit,
    utility: @Composable (EditorUtilityShell) -> Unit,
    modal: @Composable () -> Unit,
    documentHeading: String? = null,
) {
    val measuring = plan.probes == 1
    val sidecar = plan.utilities == 1 && plan.scrims == 0 && !measuring
    val sheet = plan.scrims == 1
    val modalOpen = foundationFailure == null && !measuring &&
        workspace.modal != EditorModalSurface.NONE
    val layout = workspace.renderedLayout()
    val utilityPresence = workspace.resolvedUtilityPresence()
    if (plan.editors == 1) {
        // The proposal trigger is composed inside the editor slot. Bind its cross-surface
        // relationship only after that subtree commits, including measurement remounts.
        SideEffect {
            document.getElementById(EDITOR_INLINE_ASSIST_TRIGGER_ID)
                ?.setAttribute("aria-controls", EDITOR_UTILITY_SURFACE_ID)
        }
    }
    val measuringModifier = if (measuring) {
        Modifier.styleModifier {
            property("visibility", "hidden")
            property("pointer-events", "none")
        }
    } else {
        Modifier
    }

    Div(EditorFrameStyle.toModifier().toAttrs()) {
        // The first tab stop, before the header, exactly as the app shell does it. A plain anchor
        // to a fragment rather than a router link: this has to move focus into the editor pane, and
        // the browser's own fragment behaviour is the only thing that does that without a scroll
        // handler of our own.
        // Only when the editor pane is actually mounted. In the compact layout the queue is the
        // primary view and there is no editor pane to skip to — and a skip link whose target is not
        // in the document is a tab stop that silently does nothing, which is worse than none.
        if (!measuring && plan.editors == 1) {
            A(
                href = "#$EDITOR_CONTENT_ID",
                attrs = { attr("data-skip-link", "") },
            ) {
                Text(t("editor.editor_composition.skip_to_the_editor", "Skip to the editor"))
            }
        }
        // The page's only h1, and the thing the editor never said out loud: which language, in
        // which project. Visually hidden because the header beside it already shows both — this
        // exists so a heading list and a screen-reader page summary are not empty.
        if (documentHeading != null) {
            H1(LVisuallyHiddenStyle.toModifier().toAttrs()) { Text(documentHeading) }
        }
        Div(
            EditorBackgroundStyle.toModifier()
                .attr("data-editor-background", "true")
                .then(if (sheet || modalOpen) Modifier.attr("inert", "") else Modifier)
                .then(if (sheet || modalOpen) Modifier.attr("aria-hidden", "true") else Modifier)
                .toAttrs(),
        ) {
            header()
            Div(
                EditorWorkspaceStyle.toModifier()
                    .then(
                        when {
                            plan.queues == 1 && plan.editors == 1 && plan.utilities == 1 ->
                                EditorWorkbenchTrioStyle.toModifier()

                            plan.queues == 1 && plan.editors == 1 -> EditorWorkbenchQueueStyle.toModifier()
                            plan.editors == 1 && plan.utilities == 1 -> EditorWorkbenchUtilityStyle.toModifier()
                            else -> EditorCompactWorkspaceStyle.toModifier()
                        },
                    )
                    .then(measuringModifier)
                    .toAttrs {
                        attr("data-editor-workspace", "true")
                        attr("data-editor-layout", layout.name.lowercase())
                        attr("data-editor-layout-reason", workspace.layoutReason())
                        attr("data-editor-primary-view", workspace.primaryViewHook())
                        if (measuring) {
                            attr("data-editor-measurement-probe", workspace.layoutMeasurementId.toString())
                            attr("aria-hidden", "true")
                            attr("inert", "")
                        }
                    },
            ) {
                if (foundationFailure != null) {
                    Div(EditorFoundationFailureStyle.toModifier().toAttrs()) {
                        foundationFailure()
                    }
                } else {
                    if (plan.queues == 1) queue(measuring)
                    if (plan.editors == 1) editor(measuring)
                    if (plan.utilities == 1 && !sheet) {
                        CompositionLocalProvider(LocalEditorUtilityPresence provides utilityPresence) {
                            utility(
                                when {
                                    measuring -> EditorUtilityShell.MEASURING
                                    sidecar -> EditorUtilityShell.SIDECAR
                                    else -> EditorUtilityShell.SHEET
                                },
                            )
                        }
                    }
                }
            }
        }
        if (plan.utilities == 1 && sheet) {
            Div(
                Modifier
                    .then(if (modalOpen) Modifier.attr("inert", "") else Modifier)
                    .then(if (modalOpen) Modifier.attr("aria-hidden", "true") else Modifier)
                    .toAttrs(),
            ) {
                CompositionLocalProvider(LocalEditorUtilityPresence provides utilityPresence) {
                    utility(EditorUtilityShell.SHEET)
                }
            }
        }
        // **Composed whenever the editor is live, not only while a dialog is open.**
        //
        // `EditorModalSurfaces` owns a presence lifecycle: it decides for itself when to stop
        // rendering, and holds the surface on screen through its exit. Gating the call on
        // `modalOpen` — which requires `modal != NONE` — removed that whole subtree in the same
        // frame `closeModal()` fired, taking `rememberLPresence` and the `leaving` memory with it,
        // so no EXITING frame could ever be committed. The entrance was dead the same way: first
        // composed only when a modal was already open, presence initialises straight to PRESENT.
        // Both dialogs popped in and vanished, which is exactly what the lifecycle was added to fix.
        //
        // `LModal` is composed unconditionally by its callers for this reason; the editor's version
        // was written in the same shape and then gated. It returns immediately when nothing is
        // rendered, so composing it while closed emits no DOM.
        //
        // `modalOpen` stays exactly as it is for the background `inert`/`aria-hidden` above: the
        // close transition hands focus back to an element *inside* that background, which cannot be
        // focused while the subtree is inert.
        if (foundationFailure == null && !measuring) modal()
        Span(
            LVisuallyHiddenStyle.toModifier()
                .attr("role", "status")
                .attr("aria-live", "polite")
                .attr("aria-atomic", "true")
                .toAttrs(),
        ) {
            if (announcement != null) {
                // **`key`, so a repeated sentence gets a new DOM identity.** A live region is
                // announced when its content changes; without this the Span sits at a fixed
                // composition slot and Compose updates it in place, so saving the same key twice
                // produced a byte-identical text node with only the id attribute different — and
                // most screen readers treat that as nothing having happened. The second save was
                // silent, on the action a translator repeats more than any other.
                //
                // `EditorAssistResults` already does exactly this, and
                // `EditorAssistRepeatedFeedbackBrowserTest` asserts the two nodes are not the same
                // object for the same reason.
                key(announcement.id) {
                    Span(
                        Modifier
                            .attr("data-editor-announcement-id", announcement.id.toString())
                            .toAttrs(),
                    ) {
                        Text(announcement.message)
                    }
                }
            }
        }
    }
}
